// CLASSES
import Validator from "@classes/Validator";

// HELPERS
import api from "@helpers/api";

export default class Session {
  _listeners = {};
  _data = null;
  _documents = [];
  _lookupData = null;
  _context = null;
  _appKey = null;
  _authToken = null;
  _caseId = null;

  constructor(data = {}) {
    this._data = data;
  }

  // Properties
  get data() {
    return this._data;
  }

  get documents() {
    return this._documents;
  }

  get lookupData() {
    return this._lookupData;
  }

  get ssnOrg() {
    return (
      this.lookupData && (this.lookupData.ssn || this.lookupData.orgNumber)
    );
  }

  get caseId() {
    return this._caseId;
  }

  get context() {
    return this._context;
  }

  get appKey() {
    return this._appKey;
  }

  get authToken() {
    return this._authToken;
  }

  get isCompany() {
    return typeof this._isCompany !== "undefined"
      ? this._isCompany
      : Validator.validateOrgNum(this.ssnOrg || "", true);
  }

  set isCompany(value) {
    this._isCompany = value === true;
    _notify.call(this, "change", { isCompany: value === true });
  }

  addDocument(doc) {
    const documents = [...this.documents];
    documents.push(doc);
    this._documents = documents;
  }

  removeDocumentAtIndex(index) {
    const documents = [...this.documents];
    documents.splice(index, 1);
    this._documents = documents;
  }

  // Methods
  updateData(data) {
    /**
     * Clear data for company reps component before resetting, to avoid stale data
     */
    for (const key in this._data) {
      if (key.startsWith("company_rep_")) {
        delete this._data[key];
      }
    }

    for (const key in data) {
      this._data[key] = data[key];
    }

    _notify.call(this, "change", { data });
  }

  async upload(partnerName, signOnScreen, signatories) {

    const excludedDocuments = [];
    if (this._data["kyc_company"] === "yes")
    {
      excludedDocuments.push(0); // Remove KYC private individual
    }
    else if (this._data["kyc_company"] === "no")
    {
      excludedDocuments.push(1); // Remove KYC company
    }

    const response = await api("/case/upload", {
      formFields: this._data,
      advisor: this.context != null ? this.context.partner : null,
      authToken: this._authToken,
      signOnScreen: signOnScreen,
      signatories: signatories,
      documents: this.documents,
      excludedDocuments
    });

    this._caseId = response.caseId;

    _notify.call(this, "upload", { data: this._data });
    return response;
  }

  async sendToAdvisor(data) {
    const response = await api("/case/send/advisor", data);
    return response;
  }

  dataHasChanged(data) {
    for (const key in data) {
      if (typeof this._data[key] !== "undefined" && !!data[key]) {
        if (this._data[key] !== data[key]) {
          return true;
        }
      }
    }

    return false;
  }

  async loadLookupData(data) {
    const lookupData = await api("/lookup", {
      ...data,
      authToken: this.authToken
    });

    // Add dash to ssn/orgNumber
    const ssnOrg = lookupData.ssn || lookupData.orgNumber;
    if (ssnOrg && ssnOrg !== "-") {
      this._lookupData = lookupData;
    } else {
      if (ssnOrg.indexOf("-") === -1) {
        const controlDigits = ssnOrg.substr(ssnOrg.length - 4, ssnOrg.length);
        this._lookupData[lookupData.ssn ? "ssn" : "orgNumber"] = ssnOrg.replace(
          controlDigits,
          "-" + controlDigits
        );
      }
    }

    this.isCompany = data.type === "company"; // This will lock its type
  }

  setContext(context) {
    this._context = { ...context };
  }

  setAppKey(appKey) {
    this._appKey = appKey;
  }

  setAuthToken(authToken) {
    this._authToken = authToken;
  }

  addListener(eventName, callback) {
    if (!this._listeners[eventName]) {
      this._listeners[eventName] = [];
    }
    if (this._listeners[eventName].indexOf(callback) === -1) {
      this._listeners[eventName].push(callback);
    }
  }

  removeListener(eventName, callback) {
    const callbacks = this._listeners[eventName];
    const callbackIndex = callbacks ? callbacks.indexOf(callback) : -1;

    if (callbackIndex !== -1) {
      callbacks.splice(callbackIndex, 0);
      if (callbacks.length === 0) {
        delete this._listeners[eventName];
      }
    }
  }
}

// PRIVATE FUNCTIONS
function _notify(eventName, props) {
  if (!this._listeners[eventName]) {
    return;
  }
  const callbacks = this._listeners[eventName];

  for (let i = 0, n = callbacks.length; i < n; i++) {
    callbacks[i]({ eventName, ...props });
  }
}



// WEBPACK FOOTER //
// ./src/classes/Session.js